import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const CheckoutSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState<'loading' | 'success' | 'error'>('loading');

  const paymentIntent = searchParams.get('payment_intent');
  const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
  const redirectStatus = searchParams.get('redirect_status');

  useEffect(() => {
    if (redirectStatus === 'succeeded') {
      setPaymentStatus('success');
    } else if (redirectStatus === 'failed') {
      setPaymentStatus('error');
    } else {
      // Check payment intent status if available
      if (paymentIntent) {
        checkPaymentStatus();
      } else {
        setPaymentStatus('error');
      }
    }
  }, [redirectStatus, paymentIntent]);

  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token || !paymentIntent) {
        setPaymentStatus('error');
        return;
      }

      const response = await fetch(`/api/payments/payment-intent/${paymentIntent}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'succeeded') {
          setPaymentStatus('success');
        } else {
          setPaymentStatus('error');
        }
      } else {
        setPaymentStatus('error');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  if (paymentStatus === 'loading') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Processing your payment...
          </h1>
          <p className="text-gray-600">
            Please wait while we confirm your order.
          </p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'error') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-red-600 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Failed
          </h1>
          <p className="text-gray-600 mb-6">
            There was an issue processing your payment. Please try again or contact support.
          </p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/shop')}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
            >
              Return to Shop
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="border border-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-50"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto text-center">
        <div className="text-green-600 text-6xl mb-4">✅</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Payment Successful!
        </h1>
        <p className="text-gray-600 mb-6">
          Thank you for your purchase. You will receive a confirmation email shortly.
        </p>

        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">What's Next?</h2>
          <div className="space-y-3 text-left">
            <div className="flex items-start">
              <span className="text-green-600 mr-3 mt-1">📧</span>
              <div>
                <div className="font-medium text-gray-900">Confirmation Email</div>
                <div className="text-sm text-gray-600">
                  You'll receive an order confirmation email within a few minutes.
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <span className="text-blue-600 mr-3 mt-1">📦</span>
              <div>
                <div className="font-medium text-gray-900">Order Processing</div>
                <div className="text-sm text-gray-600">
                  For physical items: We'll prepare your order for shipping within 1-2 business days.
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <span className="text-purple-600 mr-3 mt-1">🚚</span>
              <div>
                <div className="font-medium text-gray-900">Shipping</div>
                <div className="text-sm text-gray-600">
                  You'll receive tracking information once your order ships.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-x-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
          >
            View Dashboard
          </button>
          <button
            onClick={() => navigate('/shop')}
            className="border border-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-50"
          >
            Continue Shopping
          </button>
        </div>

        <div className="mt-8 text-sm text-gray-500">
          <p>
            Questions about your order? {' '}
            <button
              onClick={() => navigate('/contact')}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Contact our support team
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;